import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGame, api } from '../context/GameContext';
import { Gavel, Star, Shield, Sparkles, Crown, Diamond, Clock, Trophy, History, X } from 'lucide-react';
import ChessRook from '../components/ChessRook';
import thumb from '../utils/thumb';

const STAGE_BG = 'https://static.prod-images.emergentagent.com/jobs/06f96b1d-56bc-4d00-bf55-19b4f164e6b8/images/dd4bd7a05876224b686a3076553f53b8e21f01391c057e89188cfe9627a71725.png';

const RARITY_ICON = {
  rare: { icon: Diamond, color: '#5B68F6', label: 'RARE' },
  legendary: { icon: Crown, color: '#A855F7', label: 'LEGENDARY' },
  masterpiece: { icon: Sparkles, color: '#D4AF37', label: 'MASTERPIECE' },
};

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// Some imported titles already contain leading/trailing quotes (e.g. ""Rubens Peale...").
// Strip them so we don't double-quote on render.
const cleanTitle = (s) => {
  if (!s) return '';
  return String(s).replace(/^["'«»]+|["'«»]+$/g, '').trim();
};

const formatRemaining = (endIso) => {
  if (!endIso) return '--:--:--';
  const end = new Date(endIso).getTime();
  const ms = end - Date.now();
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const s = String(totalSec % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

// Stage curtain transition — plays once on mount of the Auction page so
// users get a theatrical opening (and the brief loading flash is hidden
// behind the closed curtains). Auto-unmounts after the animation finishes
// so it doesn't block any interactions.
const StageCurtain = () => {
  const [open, setOpen] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    // Tiny delay so the underlying stage and image start loading first.
    const t1 = setTimeout(() => setOpen(true), 100);
    // Match the CSS transition duration + a small buffer.
    const t2 = setTimeout(() => setRemoved(true), 1700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (removed) return null;

  // Velvet panel: vertical pleats via repeating-linear-gradient layered
  // over a horizontal radial sheen. Inset shadows give the rod a body.
  const pleats = `repeating-linear-gradient(
    90deg,
    rgba(0,0,0,0.32) 0px,
    rgba(0,0,0,0) 14px,
    rgba(255,255,255,0.04) 26px,
    rgba(0,0,0,0) 38px,
    rgba(0,0,0,0.32) 52px
  )`;
  const leftPanel = `${pleats}, linear-gradient(90deg, #3a0508 0%, #6f0d11 30%, #9a1418 55%, #6f0d11 80%, #3a0508 100%)`;
  const rightPanel = `${pleats}, linear-gradient(-90deg, #3a0508 0%, #6f0d11 30%, #9a1418 55%, #6f0d11 80%, #3a0508 100%)`;
  const transition = 'transform 1500ms cubic-bezier(0.7, 0, 0.2, 1)';

  return (
    <div
      data-testid="auction-stage-curtain"
      className="absolute top-0 left-0 right-0 z-40 pointer-events-none overflow-hidden"
      style={{ height: '52vh', minHeight: '340px' }}
    >
      {/* Top valance — gold-trimmed crimson swag. Slides up out of
          frame in sync with the side panels so the reveal feels
          unified instead of leaving the swag stuck mid-air. The
          parent's overflow-hidden hides it once it clears the top. */}
      <div
        className="absolute top-0 left-0 right-0 h-6 z-20"
        style={{
          background: 'linear-gradient(180deg, #6e0a0d 0%, #4a0709 100%)',
          boxShadow: '0 4px 10px rgba(0,0,0,0.55), inset 0 -2px 0 #d4af37',
          transform: open ? 'translateY(-101%)' : 'translateY(0)',
          transition,
        }}
      />

      {/* Left panel */}
      <div
        className="absolute top-0 left-0 h-full"
        style={{
          width: '52%',
          transform: open ? 'translateX(-101%)' : 'translateX(0)',
          transition,
          backgroundImage: leftPanel,
          boxShadow: 'inset -18px 0 30px rgba(0,0,0,0.55), 4px 0 12px rgba(0,0,0,0.6)',
        }}
      />

      {/* Right panel */}
      <div
        className="absolute top-0 right-0 h-full"
        style={{
          width: '52%',
          transform: open ? 'translateX(101%)' : 'translateX(0)',
          transition,
          backgroundImage: rightPanel,
          boxShadow: 'inset 18px 0 30px rgba(0,0,0,0.55), -4px 0 12px rgba(0,0,0,0.6)',
        }}
      />

      {/* Center seam highlight — fades out as curtains separate */}
      <div
        className="absolute top-0 left-1/2 h-full -translate-x-1/2 w-px"
        style={{
          background: 'linear-gradient(180deg, rgba(255,200,80,0) 0%, rgba(255,200,80,0.6) 50%, rgba(255,200,80,0) 100%)',
          opacity: open ? 0 : 0.9,
          transition: 'opacity 400ms ease-out',
        }}
      />
    </div>
  );
};


// "UP NEXT" billboard — shown on the auction empty state when the queue
// has at least one lot. Hero = #1 in queue (large, with rarity glow);
// side cards = #2 and #3 if they exist. Replaces the curator-waiting
// placeholder with a marketing moment that converts dead air into hype.
const UpNextBillboard = ({ queue, language }) => {
  // Hero = the most expensive lot in the queue (by starting_price), so the
  // empty-state billboard always advertises the strongest lot. The other two
  // most expensive go into the "Then" carousel.
  const sorted = [...queue].sort(
    (a, b) => (b.starting_price || 0) - (a.starting_price || 0)
  );
  const hero = sorted[0];
  const sides = sorted.slice(1, 3);
  const heroRarity = RARITY_ICON[hero?.rarity] || RARITY_ICON.rare;
  const HeroRarityIcon = heroRarity?.icon || Sparkles;

  return (
    <>
      {/* Stage area with the next lot pre-revealed */}
      <div className="relative w-full" style={{ height: '52vh', minHeight: '340px' }} data-testid="auction-up-next-stage">
        <img src={STAGE_BG} alt="stage" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85" />

        {/* Hero painting */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] z-10 w-[58%] max-w-[220px]">
          <img
            src={hero.image_url}
            alt={hero.title}
            className="w-full aspect-[3/4] object-cover rounded-sm auction-up-next-glow"
            style={heroRarity ? {
              boxShadow: `0 0 40px 12px ${heroRarity.color}66, 0 0 90px 24px ${heroRarity.color}33, 0 18px 30px rgba(0,0,0,0.7)`,
              outline: `1.5px solid ${heroRarity.color}88`,
              outlineOffset: '2px',
            } : undefined}
          />

          {/* "UP NEXT" badge */}
          <div
            className="absolute top-1.5 left-1.5 z-20 px-2 py-1 rounded-md text-[10px] font-bold tracking-[0.14em] flex items-center gap-1"
            style={{
              background: 'linear-gradient(135deg,#5B68F6,#7C8DF8)',
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(91,104,246,0.45)',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {language === 'ru' ? 'СКОРО' : 'UP NEXT'}
          </div>

          {/* UNIQUE badge (only admin lots are always unique; player lots
              inherit from the source card; mystery boxes are never unique) */}
          {(hero?.is_unique ?? true) && (
            <div
              title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
              className="absolute top-1.5 right-1.5 z-20 px-2 py-1 rounded-md text-[10px] font-bold tracking-[0.12em]"
              style={{
                background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                color: '#0a0a12',
                boxShadow: '0 4px 12px rgba(250,204,21,0.4)',
              }}
            >
              UNIQUE
            </div>
          )}
        </div>

        {/* "Starting any moment" pulsing chip at bottom of stage */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/55 backdrop-blur-sm border border-white/15 rounded-xl px-3 py-1.5 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
          <span className="text-xs font-semibold text-[#F5F5F7]">
            {language === 'ru' ? 'Стартует с минуты на минуту' : 'Starting any moment'}
          </span>
        </div>
      </div>

      {/* Bottom content */}
      <div className="relative z-30 px-4 pt-3 space-y-3" data-testid="auction-up-next-info">
        <div>
          <h2 className="text-sm font-semibold text-[#F5F5F7]">
            "{cleanTitle(hero.title)}"
          </h2>
          <p className="text-[10px] text-[#A1A1AA]">
            {hero.artist}{hero.year ? `, ${hero.year}` : ''}
          </p>
        </div>

        {/* Stats badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {heroRarity && (
            <div className="flex items-center gap-1 rounded-lg px-2 py-1 border" style={{ backgroundColor: `${heroRarity.color}26`, borderColor: `${heroRarity.color}55` }}>
              <HeroRarityIcon className="w-3 h-3" style={{ color: heroRarity.color }} />
              <span className="text-[10px] font-bold uppercase" style={{ color: heroRarity.color }}>{heroRarity.label}</span>
            </div>
          )}
          <div className="flex items-center gap-1 bg-[#5B68F6]/15 border border-[#5B68F6]/30 rounded-lg px-2 py-1">
            <ChessRook className="w-3 h-3 text-[#5B68F6]" />
            <span className="text-[10px] font-bold text-[#5B68F6]">Lv.{hero.level}</span>
          </div>
          <div className="flex items-center gap-1 bg-[#0C0C14] border border-[#D4AF37]/40 rounded-lg px-2 py-1">
            <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
            <span className="text-[10px] font-bold text-[#F5D07A]">
              {language === 'ru' ? 'Старт ⭐' : 'Start ⭐'} {fmt(hero.starting_price)}
            </span>
          </div>
        </div>

        {/* Side cards: #2 and #3 in queue */}
        {sides.length > 0 && (
          <div className="glass-card rounded-xl p-3 border border-white/5">
            <h3 className="text-[10px] uppercase tracking-wide text-[#A1A1AA] mb-2">
              {language === 'ru' ? 'Затем · ' : 'Then · '}{queue.length - 1}
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {sides.map((q, idx) => {
                const sideRarity = RARITY_ICON[q.rarity] || RARITY_ICON.rare;
                return (
                  <div key={q.id} className="shrink-0 w-24 text-center" data-testid={`auction-up-next-side-${idx + 2}`}>
                    <div className="relative">
                      <img src={thumb(q.image_url, 160)} alt={q.title} loading="lazy" decoding="async" className="w-24 h-24 object-cover rounded-md border border-white/10" />
                      <div
                        className="absolute -top-1 -left-1 px-1.5 rounded text-[9px] font-bold tracking-wider"
                        style={{
                          background: '#0C0C14',
                          color: '#A1A1AA',
                          border: '1px solid rgba(255,255,255,0.15)',
                          lineHeight: '1.5',
                        }}
                      >
                        #{idx + 2}
                      </div>
                    </div>
                    <p className="text-[10px] text-[#F5F5F7] mt-1 truncate font-semibold">{cleanTitle(q.title)}</p>
                    {q.year && (
                      <p className="text-[9px] text-[#A1A1AA]/80 font-mono leading-tight">{q.year}</p>
                    )}
                    <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: sideRarity.color }}>
                      {sideRarity.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          data-testid="bid-btn"
          disabled
          className="w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 bg-[#5B68F6]/15 text-[#5B68F6]/40 border border-[#5B68F6]/10 cursor-not-allowed"
        >
          <Gavel className="w-5 h-5" />
          <span>{language === 'ru' ? 'СКОРО' : 'STARTING SOON'}</span>
        </button>
      </div>

      <style>{`
        @keyframes upNextGlowPulse {
          0%, 100% { filter: brightness(0.95) saturate(0.9); transform: scale(0.985); }
          50%      { filter: brightness(1.05) saturate(1.1);  transform: scale(1.005); }
        }
        .auction-up-next-glow {
          animation: upNextGlowPulse 2.6s ease-in-out infinite;
          will-change: filter, transform;
        }
      `}</style>
    </>
  );
};

const AuctionPage = () => {
  const { user, language, haptic, t, refreshUser } = useGame();
  const [state, setState] = useState({ active: null, pending_payment: null, queue: [], recent_sales: [] });
  const [bids, setBids] = useState([]);
  const [bidInput, setBidInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [historyTab, setHistoryTab] = useState('sales'); // 'sales' | 'mine'
  const [myBids, setMyBids] = useState([]);
  const [showPayPopup, setShowPayPopup] = useState(false);
  const [tick, setTick] = useState(0);
  const lastLotIdRef = useRef(null);
  // Floating star bubbles — only animate when empty state is shown
  const [emptyBubbles, setEmptyBubbles] = useState([]);
  const bubbleIdRef = useRef(0);
  const bubbleAmountRef = useRef(800);

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get('/auction/state');
      setState(res.data);
      const cur = res.data.active || res.data.pending_payment;
      if (cur && cur.id) {
        const detail = await api.get(`/auction/lots/${cur.id}`);
        setBids(detail.data.bids || []);
      } else {
        setBids([]);
      }
    } catch (err) {}
  }, []);

  useEffect(() => {
    fetchState();
    const interval = setInterval(fetchState, 5000);
    return () => clearInterval(interval);
  }, [fetchState]);

  // Local 1-sec ticker for countdown rendering
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const lot = state.active;
  const pending = state.pending_payment;

  // Spawn floating star bubbles only while empty state is visible
  const isEmptyState = !lot && !pending;
  useEffect(() => {
    if (!isEmptyState) {
      setEmptyBubbles([]);
      return;
    }
    const spawn = () => {
      const id = bubbleIdRef.current++;
      const left = 18 + Math.random() * 64;
      const amount = bubbleAmountRef.current;
      bubbleAmountRef.current += Math.floor(50 + Math.random() * 150);
      setEmptyBubbles((prev) => [...prev.slice(-1), { id, amount, left }]);
      setTimeout(() => {
        setEmptyBubbles((prev) => prev.filter((b) => b.id !== id));
      }, 3000);
    };
    const interval = setInterval(spawn, 2200 + Math.random() * 1800);
    return () => clearInterval(interval);
  }, [isEmptyState]);
  const isYouLeading = lot && user && lot.current_bidder_id === user.id;
  const isYouPaying = pending && user && pending.payment_target_id === user.id;

  // Auto-open pay popup when YOU become the payment target
  useEffect(() => {
    if (isYouPaying && lastLotIdRef.current !== pending.id) {
      setShowPayPopup(true);
      lastLotIdRef.current = pending.id;
      try { haptic?.('success'); } catch {}
    }
  }, [isYouPaying, pending, haptic]);

  const placeBid = async (amount) => {
    if (!lot || busy) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/auction/lots/${lot.id}/bid`, { amount });
      try { haptic?.('success'); } catch {}
      setBidInput('');
      await fetchState();
    } catch (err) {
      setError(err.response?.data?.detail || 'Bid failed');
      try { haptic?.('error'); } catch {}
    } finally {
      setBusy(false);
    }
  };

  const placeBuyout = async () => {
    if (!lot || busy) return;
    if (!window.confirm(`Buy now for ${fmt(lot.buyout_price)} ⭐?`)) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/auction/lots/${lot.id}/buyout`);
      try { haptic?.('success'); } catch {}
      await fetchState();
    } catch (err) {
      setError(err.response?.data?.detail || 'Buyout failed');
    } finally {
      setBusy(false);
    }
  };

  const handleNotifyUsers = async () => {
    if (!lot || notifying) return;
    const isRu = language === 'ru';
    if (!window.confirm(isRu
      ? 'Оповестить всех игроков о вашем лоте за 25 ⭐? Сообщение отправится сразу после оплаты.'
      : 'Broadcast your lot to all players for 25 ⭐? The alert will go out right after payment.')) return;
    setNotifying(true);
    setError('');
    try {
      const { data } = await api.post('/auction/sell/notify-users', { lot_id: lot.id });
      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) {
        window.open(data.invoice_link, '_blank');
        setNotifying(false);
        return;
      }
      tg.openInvoice(data.invoice_link, async (status) => {
        if (status === 'paid') {
          try { haptic?.('success'); } catch {}
          await new Promise(r => setTimeout(r, 2000));
          await fetchState();
        }
        setNotifying(false);
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Notify users failed');
      setNotifying(false);
    }
  };

  const handleWithdraw = async () => {
    if (!lot || withdrawing) return;
    setError('');
    // Get fee preview first
    let preview;
    try {
      const { data } = await api.get(`/auction/sell/withdraw/preview/${lot.id}`);
      preview = data;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not preview withdraw');
      return;
    }
    const isRu = language === 'ru';
    const feeText = preview.fee_stars > 0
      ? (isRu ? `Стоимость: ${preview.fee_stars} ⭐ (20% от текущей ставки)` : `Fee: ${preview.fee_stars} ⭐ (20% of current bid)`)
      : (isRu ? 'Бесплатно (ставок нет)' : 'Free (no bids yet)');
    const bidderText = preview.bidders_to_notify > 0
      ? (isRu ? `\n${preview.bidders_to_notify} участник(ов) получат уведомление.` : `\n${preview.bidders_to_notify} bidder(s) will be notified.`)
      : '';
    const confirmText = (isRu
      ? `Снять лот с аукциона?\n\n${feeText}${bidderText}\n\nNFT вернётся вам.`
      : `Withdraw this lot?\n\n${feeText}${bidderText}\n\nNFT will be returned to you.`);
    if (!window.confirm(confirmText)) return;

    setWithdrawing(true);
    try {
      const { data } = await api.post('/auction/sell/withdraw', { lot_id: lot.id });
      if (data.free) {
        try { haptic?.('success'); } catch {}
        await fetchState();
        return;
      }
      // Paid path: open Telegram Stars invoice
      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) {
        window.open(data.invoice_link, '_blank');
        setWithdrawing(false);
        return;
      }
      tg.openInvoice(data.invoice_link, async (status) => {
        if (status === 'paid') {
          try { haptic?.('success'); } catch {}
          // Give webhook ~2s, then refresh
          await new Promise(r => setTimeout(r, 2200));
          await fetchState();
        } else if (status === 'cancelled' || status === 'failed') {
          // Roll back the lock so bidders can resume
          try {
            await api.post('/auction/sell/withdraw/cancel', { lot_id: lot.id });
          } catch {}
          await fetchState();
        }
        setWithdrawing(false);
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Withdraw failed');
      setWithdrawing(false);
    }
  };

  const handlePay = async () => {
    if (!pending || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/auction/lots/${pending.id}/pay`);
      const link = res.data.invoice_link;
      const tg = window.Telegram?.WebApp;
      if (tg && tg.openInvoice) {
        tg.openInvoice(link, async (status) => {
          if (status === 'paid') {
            setShowPayPopup(false);
            await refreshUser?.();
            await fetchState();
          }
        });
      } else {
        window.open(link, '_blank');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start payment');
    } finally {
      setBusy(false);
    }
  };

  const rarityInfo = (lot || pending) ? RARITY_ICON[(lot || pending).rarity] || RARITY_ICON.rare : null;
  const RarityIcon = rarityInfo?.icon || Sparkles;
  const minNext = lot?.min_next_bid || 0;

  return (
    <div className="min-h-screen bg-[#05050A] pb-20 relative overflow-hidden" data-testid="auction-page">
      {/* Theatrical curtain reveal — plays once on Auction page mount.
          Hides the brief loading flash behind closed velvet panels, then
          parts to reveal the stage. Auto-unmounts after the animation. */}
      <StageCurtain />

      {/* History toggle */}
      <button
        onClick={() => setShowHistory((v) => !v)}
        className="absolute top-3 right-3 z-40 w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-[#F5F5F7] hover:bg-white/15 transition"
        data-testid="auction-history-toggle"
      >
        <History className="w-4 h-4" />
      </button>

      {/* ---- empty state — when no active and no pending lot.
              If there are queued lots, show an "UP NEXT" billboard with the
              first lot prominently on stage + the next 2 as side cards.
              Otherwise fall back to the curator-waiting copy. ---- */}
      {!lot && !pending && (
        <>
          {state.queue?.length > 0 ? (
            <UpNextBillboard queue={state.queue} language={language} />
          ) : (
            <>
              <div className="relative w-full" style={{ height: '52vh', minHeight: '340px' }}>
                <img src={STAGE_BG} alt="stage" className="absolute inset-0 w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85" />

                {/* Spotlight only — no easel placeholder. Keeps stage looking
                    like a real auction hall waiting for the next reveal. */}

                {/* Floating star bubbles — tiny ambient animation */}
                {emptyBubbles.map(b => (
                  <div
                    key={b.id}
                    className="absolute z-20 animate-bubble-rise pointer-events-none"
                    style={{ left: `${b.left}%`, bottom: '5%' }}
                  >
                    <div className="bg-white/90 text-[#0C0C14] text-[11px] font-bold rounded-xl px-2.5 py-1 shadow-lg flex items-center gap-1">
                      <span>⭐</span>
                      <span>{b.amount}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative z-30 px-4 pt-3 space-y-3">
                <div>
                  <h2 className="text-sm font-semibold text-[#F5F5F7]">
                    {language === 'ru' ? 'Аукцион' : 'Auction Hall'}
                  </h2>
                  <p className="text-[10px] text-[#A1A1AA]">
                    {language === 'ru' ? 'Сейчас ни одного лота нет' : 'No lots available right now'}
                  </p>
                </div>

                <div className="glass-card rounded-xl p-3 border border-white/5">
                  <p className="text-xs text-[#A1A1AA] leading-relaxed">
                    {language === 'ru'
                      ? 'Ожидайте добавления картин от кураторов. Новые лоты появятся здесь, как только их выставят на торги.'
                      : 'New lots arrive when curators add paintings. Check back soon.'}
                  </p>
                </div>

                <button
                  data-testid="bid-btn"
                  disabled
                  className="w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 bg-[#5B68F6]/15 text-[#5B68F6]/40 border border-[#5B68F6]/10 cursor-not-allowed"
                >
                  <Gavel className="w-5 h-5" />
                  <span>BID</span>
                  <span>⭐</span>
                </button>
              </div>

              <style>{`
                @keyframes bubbleRise {
                  0% { transform: translateY(0) scale(0.6); opacity: 0; }
                  15% { transform: translateY(-20px) scale(1.1); opacity: 1; }
                  30% { transform: translateY(-50px) scale(1); opacity: 1; }
                  100% { transform: translateY(-160px) scale(0.85); opacity: 0; }
                }
                .animate-bubble-rise { animation: bubbleRise 3s ease-out forwards; }
              `}</style>
            </>
          )}
        </>
      )}

      {/* ---- main lot view ---- */}
      {(lot || pending) && (
        <>
          {/* Stage area */}
          <div className="relative w-full" style={{ height: '52vh', minHeight: '340px' }}>
            <img src={STAGE_BG} alt="stage" className="absolute inset-0 w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85" />

            {/* Painting */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] z-10 w-[58%] max-w-[220px]">
              <img
                src={(lot || pending).image_url}
                alt={(lot || pending).title}
                className="w-full aspect-[3/4] object-cover rounded-sm auction-rarity-glow"
                style={rarityInfo ? {
                  boxShadow: `0 0 40px 12px ${rarityInfo.color}88, 0 0 90px 24px ${rarityInfo.color}44, 0 0 140px 40px ${rarityInfo.color}22, 0 18px 30px rgba(0,0,0,0.7)`,
                  outline: `1.5px solid ${rarityInfo.color}aa`,
                  outlineOffset: '2px',
                } : undefined}
                data-testid="auction-painting"
              />
              {/* UNIQUE badge — only when the lot represents a unique card
                  (admin auctions always; player lots only if the source card
                  was itself a unique auction win) */}
              {((lot || pending)?.is_unique ?? true) && (
                <div
                  data-testid="auction-unique-badge"
                  title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
                  className="absolute top-1.5 left-1.5 z-20 px-2 py-1 rounded-md text-[10px] font-bold tracking-[0.12em]"
                  style={{
                    background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                    color: '#0a0a12',
                    boxShadow: '0 4px 12px rgba(250,204,21,0.4)',
                  }}
                >
                  UNIQUE
                </div>
              )}
            </div>

            {/* Live countdown */}
            {lot && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/55 backdrop-blur-sm border border-white/15 rounded-xl px-3 py-1.5 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#F5D07A]" />
                <span className="text-xs font-mono font-semibold text-[#F5F5F7]" data-testid="auction-countdown">
                  {formatRemaining(lot.effective_end_at)}
                </span>
              </div>
            )}
            {pending && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-[#22C55E]/20 backdrop-blur-sm border border-[#22C55E]/40 rounded-xl px-3 py-1.5">
                <span className="text-xs font-bold text-[#22C55E]">PAYMENT PENDING</span>
              </div>
            )}
          </div>

          {/* Bottom content */}
          <div className="relative z-30 px-4 pt-3 space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-[#F5F5F7]" data-testid="auction-lot-title">
                "{cleanTitle((lot || pending).title)}"
              </h2>
              <p className="text-[10px] text-[#A1A1AA]">
                {(lot || pending).artist}{(lot || pending).year ? `, ${(lot || pending).year}` : ''}
              </p>
              {((lot || pending).lot_type === 'player' && (lot || pending).seller_username) && (
                <p className="text-[10px] text-[#A855F7] mt-0.5 font-medium" data-testid="auction-lot-seller">
                  {language === 'ru' ? 'Продавец' : 'Listed by'}: @{(lot || pending).seller_username}
                </p>
              )}
              {(lot || pending).lot_type === 'mystery_box' && (
                <p className="text-[10px] text-[#F59E0B] mt-0.5 font-medium uppercase tracking-wider" data-testid="auction-lot-mystery">
                  🎁 {language === 'ru' ? 'Mystery Box' : 'Mystery Box'} · {(lot || pending).mystery_prize_type}
                </p>
              )}
            </div>

            {/* Stats badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {rarityInfo && (
                <div className="flex items-center gap-1 rounded-lg px-2 py-1 border" style={{ backgroundColor: `${rarityInfo.color}26`, borderColor: `${rarityInfo.color}55` }}>
                  <RarityIcon className="w-3 h-3" style={{ color: rarityInfo.color }} />
                  <span className="text-[10px] font-bold uppercase" style={{ color: rarityInfo.color }}>{rarityInfo.label}</span>
                </div>
              )}
              <div className="flex items-center gap-1 bg-[#22C55E]/15 border border-[#22C55E]/30 rounded-lg px-2 py-1">
                <Shield className="w-3 h-3 text-[#22C55E]" />
                <span className="text-[10px] font-bold text-[#22C55E]">100%</span>
              </div>
              <div className="flex items-center gap-1 bg-[#5B68F6]/15 border border-[#5B68F6]/30 rounded-lg px-2 py-1">
                <ChessRook className="w-3 h-3 text-[#5B68F6]" />
                <span className="text-[10px] font-bold text-[#5B68F6]">Lv.{(lot || pending).level}</span>
              </div>
            </div>

            {/* Current bid panel */}
            <div className="glass-card rounded-xl p-3 border border-white/5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide text-[#A1A1AA]">Current bid</span>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                  <span className="text-base font-bold text-[#F5F5F7]" data-testid="auction-current-bid">
                    {fmt(lot?.current_bid || pending?.current_bid || lot?.starting_price || 0)}
                  </span>
                </div>
              </div>
              {lot?.current_bidder_username && (
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#A1A1AA]">Leader</span>
                  <span className={`font-semibold ${isYouLeading ? 'text-[#22C55E]' : 'text-[#F5F5F7]'}`}>
                    @{lot.current_bidder_username}{isYouLeading ? ' (you)' : ''}
                  </span>
                </div>
              )}
              {lot && (
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#A1A1AA]">Min next bid</span>
                  <span className="font-mono font-semibold text-[#F5D07A]">⭐ {fmt(minNext)}</span>
                </div>
              )}
              {lot && lot.buyout_price > (lot.current_bid || 0) && (
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#A1A1AA]">Buyout</span>
                  <span className="font-mono font-semibold text-[#A855F7]">⭐ {fmt(lot.buyout_price)}</span>
                </div>
              )}
            </div>

            {/* Bid input + actions */}
            {lot && !isYouLeading && (
              <div className="space-y-2">
                {lot.lot_type === 'player' && !user?.ton_wallet && (
                  <div
                    className="text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg px-3 py-2 flex items-start gap-2"
                    data-testid="auction-wallet-required-hint"
                  >
                    <span>⚠️</span>
                    <span>
                      {language === 'ru'
                        ? <>Подключите <strong>TON-кошелёк</strong> в профиле, чтобы ставить на NFT-лоты — иначе картина не сможет прийти к вам.</>
                        : <>Connect your <strong>TON wallet</strong> in Profile to bid on NFT lots — otherwise the painting cannot reach you.</>}
                    </span>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder={`≥ ${minNext}`}
                    value={bidInput}
                    onChange={(e) => setBidInput(e.target.value)}
                    className="flex-1 bg-[#141420] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-[#F5F5F7] placeholder:text-[#5A5A66] focus:outline-none focus:border-[#5B68F6]"
                    data-testid="auction-bid-input"
                  />
                  <button
                    onClick={() => placeBid(parseInt(bidInput, 10))}
                    disabled={busy || !bidInput || parseInt(bidInput, 10) < minNext}
                    className="px-4 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1 bg-[#5B68F6] text-white disabled:bg-[#5B68F6]/30 disabled:text-white/50"
                    data-testid="auction-bid-btn"
                  >
                    <Gavel className="w-4 h-4" />
                    BID
                  </button>
                </div>
                <button
                  onClick={() => placeBid(minNext)}
                  disabled={busy}
                  className="w-full py-2 rounded-xl text-xs font-semibold border border-white/15 text-[#F5F5F7] hover:bg-white/5 disabled:opacity-50"
                  data-testid="auction-bid-min-btn"
                >
                  Bid minimum (⭐ {fmt(minNext)})
                </button>
                {lot.buyout_price > (lot.current_bid || 0) && (
                  <button
                    onClick={placeBuyout}
                    disabled={busy}
                    className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-[#A855F7] text-white disabled:opacity-50"
                    data-testid="auction-buyout-btn"
                  >
                    <Trophy className="w-4 h-4" />
                    BUY NOW · ⭐ {fmt(lot.buyout_price)}
                  </button>
                )}
              </div>
            )}

            {lot && isYouLeading && (
              <div className="text-center py-2 px-3 rounded-xl bg-[#22C55E]/15 border border-[#22C55E]/30 text-xs font-semibold text-[#22C55E]">
                ✓ You are the highest bidder
              </div>
            )}

            {/* Seller-only: withdraw their own lot */}
            {lot && lot.lot_type === 'player' && lot.seller_id === user?.id && (
              <div className="space-y-2">
                {!lot.notify_sent && lot.status === 'active' && (
                  <button
                    onClick={handleNotifyUsers}
                    disabled={notifying}
                    data-testid="auction-notify-btn"
                    className="w-full py-2 rounded-xl text-xs font-semibold border border-[#FACC15]/40 text-[#FACC15] hover:bg-[#FACC15]/10 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {notifying
                      ? (language === 'ru' ? 'Отправка…' : 'Sending…')
                      : (language === 'ru'
                        ? '📣 Оповестить всех игроков (25 ⭐)'
                        : '📣 Notify all players (25 ⭐)')}
                  </button>
                )}
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  data-testid="auction-withdraw-btn"
                  className="w-full py-2 rounded-xl text-xs font-semibold border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 active:scale-95 transition-all disabled:opacity-50"
                >
                  {withdrawing
                    ? (language === 'ru' ? 'Снятие…' : 'Withdrawing…')
                    : (lot.current_bid > 0
                      ? (language === 'ru'
                        ? `↩ Снять с аукциона (${Math.max(1, Math.round(lot.current_bid * 0.2))} ⭐)`
                        : `↩ Withdraw (${Math.max(1, Math.round(lot.current_bid * 0.2))} ⭐ fee)`)
                      : (language === 'ru' ? '↩ Снять с аукциона (бесплатно)' : '↩ Withdraw (free)')
                    )}
                </button>
              </div>
            )}

            {error && (
              <div className="text-center py-2 px-3 rounded-xl bg-[#EF4444]/15 border border-[#EF4444]/30 text-xs text-[#EF4444]">
                {error}
              </div>
            )}

            {/* Bid log */}
            {bids.length > 0 && (
              <div className="glass-card rounded-xl p-3 border border-white/5">
                <h3 className="text-[10px] uppercase tracking-wide text-[#A1A1AA] mb-2">Bid history</h3>
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {bids.map((b) => {
                    // While the lot is in payment-pending, flag bidders who
                    // have already been attempted in the cascade AND are no
                    // longer the current target — they failed to pay in time.
                    const attempted = (pending?.cascade_attempted || []);
                    const currentTarget = pending?.payment_target_id;
                    const inFlightFail = !!pending
                      && attempted.includes(b.user_id)
                      && b.user_id !== currentTarget;
                    // Persisted failure on the bid record itself — survives
                    // cascade resume / buyout failure (the lot returns to
                    // active so `pending` is null, but the bid still needs
                    // to render crossed out).
                    const persistedFail = b.status === 'failed_payment';
                    const failedToPay = inFlightFail || persistedFail;
                    return (
                      <div
                        key={b.id}
                        className="flex items-center justify-between text-[11px]"
                        data-testid={failedToPay ? 'bid-failed-row' : 'bid-row'}
                      >
                        <span
                          className={`truncate ${failedToPay ? 'text-[#EF4444]' : 'text-[#F5F5F7]'}`}
                          style={failedToPay ? { textDecoration: 'line-through' } : undefined}
                          title={failedToPay
                            ? (language === 'ru' ? 'Не оплатил в срок' : 'Did not pay in time')
                            : undefined}
                        >
                          @{b.username || '—'}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          {b.kind === 'buyout' && <Trophy className={`w-3 h-3 ${failedToPay ? 'text-[#EF4444]' : 'text-[#A855F7]'}`} />}
                          {failedToPay && <X className="w-3 h-3 text-[#EF4444]" />}
                          <Star className={`w-3 h-3 ${failedToPay ? 'text-[#EF4444]/60' : 'text-[#D4AF37] fill-[#D4AF37]'}`} />
                          <span
                            className={`font-mono font-semibold ${failedToPay ? 'text-[#EF4444]/80' : 'text-[#F5F5F7]'}`}
                            style={failedToPay ? { textDecoration: 'line-through' } : undefined}
                          >
                            {fmt(b.amount)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Up next */}
            {state.queue?.length > 0 && (
              <div className="glass-card rounded-xl p-3 border border-white/5">
                <h3 className="text-[10px] uppercase tracking-wide text-[#A1A1AA] mb-2">Coming up · {state.queue.length}</h3>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {state.queue.map((q) => {
                    const qRarity = RARITY_ICON[q.rarity] || RARITY_ICON.rare;
                    return (
                      <div key={q.id} className="shrink-0 w-20 text-center">
                        <img src={thumb(q.image_url, 160)} alt={q.title} loading="lazy" decoding="async" className="w-20 h-20 object-cover rounded-md border border-white/10" />
                        <p className="text-[9px] text-[#A1A1AA] mt-1 truncate">{cleanTitle(q.title)}</p>
                        {q.year && (
                          <p className="text-[9px] text-[#A1A1AA]/70 font-mono leading-tight">{q.year}</p>
                        )}
                        <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: qRarity.color }}>
                          {qRarity.label}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* History panel */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={() => setShowHistory(false)}>
          <div className="min-h-screen px-4 py-6 max-w-md mx-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-[#F5F5F7]">Auction history</h2>
              <button onClick={() => setShowHistory(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <X className="w-4 h-4 text-[#F5F5F7]" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setHistoryTab('sales')}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${historyTab === 'sales' ? 'bg-[#5B68F6] text-white' : 'bg-white/5 text-[#A1A1AA]'}`}
                data-testid="auction-history-tab-sales"
              >
                Recent sales
              </button>
              <button
                onClick={async () => {
                  setHistoryTab('mine');
                  try {
                    const res = await api.get('/auction/my-bids');
                    setMyBids(res.data.bids || []);
                  } catch {}
                }}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${historyTab === 'mine' ? 'bg-[#5B68F6] text-white' : 'bg-white/5 text-[#A1A1AA]'}`}
                data-testid="auction-history-tab-mine"
              >
                My bids
              </button>
            </div>

            {historyTab === 'sales' && (
              <>
                {!state.recent_sales?.length && (
                  <p className="text-center text-xs text-[#A1A1AA] py-12">No sales yet.</p>
                )}
                <div className="space-y-2">
                  {state.recent_sales?.map((s) => (
                    <div key={s.id} className="glass-card rounded-xl p-3 border border-white/5 flex items-center gap-3">
                      <div className="relative shrink-0">
                        <img src={`/api/images/${s.template_id}`} alt={s.title} className="w-12 h-12 object-cover rounded-md" />
                        <div
                          title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
                          className="absolute -top-1 -right-1 px-1 rounded text-[7px] font-bold tracking-wider"
                          style={{
                            background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                            color: '#0a0a12',
                            boxShadow: '0 0 6px rgba(250,204,21,0.5)',
                            letterSpacing: '0.08em',
                            lineHeight: '1.4',
                          }}
                        >
                          U
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[#F5F5F7] truncate">"{cleanTitle(s.title)}"</p>
                        <p className="text-[10px] text-[#A1A1AA] truncate">
                          to @{s.buyer_username || '—'} · {(s.rarity || '').toUpperCase()}
                          {s.year ? <span className="font-mono"> · {s.year}</span> : null}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Star className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37]" />
                        <span className="text-sm font-bold text-[#F5F5F7]">{fmt(s.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {historyTab === 'mine' && (
              <>
                {!myBids.length && (
                  <p className="text-center text-xs text-[#A1A1AA] py-12">You haven't bid on any lots yet.</p>
                )}
                <div className="space-y-2">
                  {myBids.map((b) => {
                    let badge = null;
                    if (b.i_won) {
                      badge = <span className="text-[10px] font-bold text-[#22C55E]">WON</span>;
                    } else if (b.outcome === 'sold') {
                      badge = <span className="text-[10px] font-bold text-[#EF4444]">OUTBID</span>;
                    } else if (b.outcome === 'active' || b.outcome === 'queued') {
                      badge = <span className="text-[10px] font-bold text-[#F5D07A]">LIVE</span>;
                    } else if (b.outcome === 'pending_payment') {
                      badge = <span className="text-[10px] font-bold text-[#A855F7]">PAYMENT</span>;
                    } else if (b.outcome === 'cleared') {
                      badge = <span className="text-[10px] font-bold text-[#A1A1AA]">CLEARED</span>;
                    }
                    return (
                      <div key={b.lot_id} className="glass-card rounded-xl p-3 border border-white/5 flex items-center gap-3">
                        {b.image_url && (
                          <img src={thumb(b.image_url, 160)} alt={b.title} loading="lazy" decoding="async" className="w-12 h-12 object-cover rounded-md shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-[#F5F5F7] truncate">"{cleanTitle(b.title)}"</p>
                            {badge}
                          </div>
                          <p className="text-[10px] text-[#A1A1AA] truncate">
                            {b.year ? <span className="font-mono">{b.year} · </span> : null}
                            Your max: ⭐ {fmt(b.my_max_bid)}
                            {b.outcome === 'sold' && b.winner_username ? ` · sold to @${b.winner_username} for ⭐ ${fmt(b.sold_amount)}` : ''}
                            {(b.outcome === 'active' || b.outcome === 'queued') && b.current_bidder_username ? ` · leader @${b.current_bidder_username} ⭐ ${fmt(b.current_bid)}` : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pay popup — auto-opens when YOU become the payment target */}      {showPayPopup && pending && isYouPaying && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-[#0C0C14] border border-[#22C55E]/40 rounded-2xl p-5 max-w-sm w-full text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-[#22C55E]" />
            </div>
            <h2 className="text-base font-bold text-[#F5F5F7] mb-1">You won the lot!</h2>
            <p className="text-xs text-[#A1A1AA] mb-4">
              "{cleanTitle(pending.title)}" · {pending.artist}
              {pending.year ? <span className="font-mono">, {pending.year}</span> : null}
            </p>
            <div className="flex items-center justify-center gap-1 mb-4">
              <Star className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
              <span className="text-2xl font-bold text-[#F5F5F7]">{fmt(pending.payment_amount)}</span>
            </div>
            <p className="text-[10px] text-[#A1A1AA] mb-4">
              Pay within 5 min or the lot passes to the next bidder.
            </p>
            <button
              onClick={handlePay}
              disabled={busy}
              className="w-full py-3 rounded-xl bg-[#22C55E] text-white font-bold disabled:opacity-50"
              data-testid="auction-pay-btn"
            >
              Pay ⭐ {fmt(pending.payment_amount)}
            </button>
            <button
              onClick={() => setShowPayPopup(false)}
              className="w-full mt-2 py-2 rounded-xl text-xs text-[#A1A1AA]"
            >
              Later
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes auctionGlowPulse {
          0%, 100% { filter: brightness(1) saturate(1); transform: scale(1); }
          50%      { filter: brightness(1.06) saturate(1.15); transform: scale(1.012); }
        }
        .auction-rarity-glow {
          animation: auctionGlowPulse 3.2s ease-in-out infinite;
          will-change: filter, transform;
        }
      `}</style>
    </div>
  );
};

export default AuctionPage;
